import React from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'

import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { RichFilterGroupsDisplay } from '~/components/features/RichFilterGroupsDisplay'
import {
  type FigureInfo,
  FigureInfoHelpers,
  type FigureInfoKey,
  figureInfoKeyLabelI18nKeyMap,
  isTranslatableFigureInfoKey,
  toContentOrNull,
} from '~/utils'
import { exists } from '~/utils'

import { FigureInfoGroupFilters } from './FigureInfoGroupFilters'
import { FigureInfoOverlapGroupFilters } from './FigureInfoOverlapGroupFilters'

export type FigureInfoRowProps = {
  figureInfo: FigureInfo
  figureInfoKey: FigureInfoKey
}

export const FigureInfoRow = ({
  figureInfo,
  figureInfoKey,
}: FigureInfoRowProps) => {
  const { t } = useTranslation()
  const content = React.useMemo(
    () =>
      match(figureInfoKey)
        .with('groupFilters', (figureInfoKey) =>
          toContentOrNull(figureInfo[figureInfoKey], (groupFilters) => (
            <FigureInfoGroupFilters groupFilters={groupFilters} />
          )),
        )
        .with('overlapGroupFilters', (figureInfoKey) =>
          toContentOrNull(figureInfo[figureInfoKey], (overlapGroupFilters) => (
            <FigureInfoOverlapGroupFilters
              overlapGroupFilters={overlapGroupFilters}
            />
          )),
        )
        .with('surveyFilter', 'surveyGroupFilter', (figureInfoKey) =>
          toContentOrNull(figureInfo[figureInfoKey], (filterTextGroups) => (
            <RichFilterGroupsDisplay filterTextGroups={filterTextGroups} />
          )),
        )
        .otherwise((figureInfoKey) => {
          const rawValue =
            figureInfoKey === 'screenLabel'
              ? FigureInfoHelpers.toScreenLabel(figureInfo)
              : figureInfo[figureInfoKey]

          // 翻訳対象キーの場合は正規化＆翻訳
          const value =
            isTranslatableFigureInfoKey(figureInfoKey) &&
            typeof rawValue === 'string'
              ? FigureInfoHelpers.translateFigureInfoValue(
                  figureInfoKey,
                  rawValue,
                  t,
                )
              : rawValue

          return toContentOrNull(value, (value) => {
            if (typeof value === 'string') {
              return <Typography variant="normal">{value}</Typography>
            }

            return (
              <VStack spacing={1}>
                {value.map((text) => (
                  <Typography key={text} variant="normal">
                    {text}
                  </Typography>
                ))}
              </VStack>
            )
          })
        }),
    [figureInfo, figureInfoKey, t],
  )

  if (!exists(content)) {
    return null
  }

  return (
    <>
      <Typography variant="normal">
        {t(figureInfoKeyLabelI18nKeyMap[figureInfoKey])}
      </Typography>

      {content}
    </>
  )
}
